import { isEqual } from 'underscore';
import * as uuid from 'uuid';


import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { DistributionApiModel } from '../../../api/distributions/model/distribution-api.model';
import { DeliverySchedule, ScheduleType } from '../../../Common/Forms/components/DeliveryScheduleInput/models/delivery-schedule';
import { RecipientInformationModel } from './recipient-information.model';
import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { DistributionBlockApiModel } from '../../../api/distributions/model/distribution-block-api.model';
import { DistributionBlockTypeApiEnum } from '../../../api/distributions/enum/distribution-block-type-api.enum';
import { RichTextDisplayElementModel } from '../../../Common/PageBuilder/models/rich-text-display-element.model';
import { ImageDisplayElementModel } from '../../../Common/PageBuilder/models/image-display-element.model';
import { distributionBlockTypeEnumToBlockTypeMap, distributionFooterBlockType } from '../../../Common/TargetingShared/model/distribution-block-types.model';
import {
    distributionBlockTypeApiEnumToDistributionBlockTypeEnum,
    DistributionBlockTypeEnum,
    distributionBlockTypeEnumToDistributionBlockTypeApiEnum
} from '../enums/distribution-block-type.enum';
import { DistributionTextDisplayElementApiModel } from '../../../api/distributions/model/distribution-text-display-element-api.model';
import { DistributionImageDisplayElementApiModel } from '../../../api/distributions/model/distribution-image-display-element-api.model';
import { FooterModel } from '../../../Common/TargetingShared/model/footer.model';
import { FooterDisplayElementModel } from '../../../Common/TargetingShared/model/footer-display-element.model';
import { ImageDisplayElementApiModel } from '../../../api/pagebuilder/models/image-display-element-api.model';
import { DistributionTitleDisplayElementApiModel } from '../../../api/distributions/model/distribution-title-display-element-api.model';
import { DividerDisplayElementModel } from '../../../Common/PageBuilder/models/divider-display-element.model';
import { DistributionDividerDisplayElementApiModel } from '../../../api/distributions/model/distribution-divider-display-element-api.model';
import { DistributionLinkDisplayElementApiModel } from '../../../api/distributions/model/distribution-link-display-element-api.model';
import { DistributionStyleStateEnum } from '../../../Common/TargetingShared/enum/distribution-style-state.enum';
import { StyleStateEnum } from '../../../Common/PageBuilder/enums/style-state.enum';
import { ButtonDisplayElementModel } from '../../../Common/PageBuilder/models/button-display-element.model';
import { DistributionButtonDisplayElementApiModel } from '../../../api/distributions/model/distribution-button-display-element-api.model';
import { LinkModel } from '../../../Common/Forms/models/link.model';
import { DistributionGeneralStylesModel } from './Distribution/distribution-general-styles.model';
import { AmplificationStatusEnum} from '../enums/amplification-status.enum';
import { DistributionStatus } from '../../../Common/TargetingShared/enum/distribution-status.enum';
import { ExcludedRecipientApiModel } from '../../../api/distributions/model/excluded-recipient.api.model';
import { RecipientType } from '../enums/recipient-type.enum';
import { LinkDisplayElementModel } from '../../../Common/PageBuilder/models/link-display-element.model';
import { DistributionType } from '../../../Common/TargetingShared/enum/distribution-type.enum';
import { BaseModel } from '../../../Common/Basic/models/base.model';
import { MergeTagModel } from '../../../Discover/Core/models/merge-tag.model';
import { LayoutBlock } from '../../../Common/PageBuilder/models/layout-block.model';
import { LayoutDisplayElement } from '../../../Common/PageBuilder/models/layout-display-element.model';
import { LayoutBlockType } from '../../../Common/PageBuilder/models/layout-block-type.model';
import { DistributionSendFrom } from './distribution-send-from.model';


export const defaultFooterStyles = {
    'alignment': 'center',
    'backgroundColor': '#C7C7C7',
    'bold': false,
    'color': '#000000',
    'fontSize': '12px'
} as any;


export class DistributionModel extends BaseModel<DistributionModel, DistributionApiModel> implements AuditableModel {

    deliverySchedule: DeliverySchedule = new DeliverySchedule(null, null);

    createdByUserId?: number;
    createdDate?: Date;
    modifiedByUserId?: number;
    modifiedDate?: Date;
    sentNow?: boolean;
    sentDate?: Date;
    sentUserId?: number;
    status: DistributionStatus = DistributionStatus.draft;
    type: DistributionType = DistributionType.DISTRIBUTION;
    sourceDistributionName?: string;

    hidden?: boolean;

    amplification?: AmplificationStatusEnum[];
    onlineBoostApproved: boolean = false;
    footerStyles: any;

    fromName: string;
    replyEmail: string;
    subjectLine: string;
    previewText: string;

    recipientInformation: RecipientInformationModel;
    attachments: string[] = [];
    localAttachmentChanges: boolean = false;
    confirmationRecipients: string[] = [];
    blocks: BlockModel[] = [];
    // @TODO: use a general styles model
    generalStyles: DistributionGeneralStylesModel;
    // @Hack: get rid of this once there's a general styles object to define the defaults

    guid: string;
    aiReferenceId: string;
    includeViewInBrowserLink: boolean = true;
    viewInBrowserLinkText: string;
    clickTracking: boolean = true;
    openTracking: boolean = true;

    footer: FooterModel = new FooterModel();
    bodyPlainText: string;
    footerPlainText: string;

    individualRecipientsListId: number;
    recommendedRecipientsListId: number;

    excludedRecipients: ExcludedRecipientApiModel[] = [];

    /** @deprecated in favor of 'fallbackPersonalizations' */
    mergeTags: MergeTagModel[] = [];

    fallbackPersonalizations: MergeTagModel[] = [];

    groupId?: number;
    isFavorite: boolean = false;

    sendFrom: DistributionSendFrom;

    includeFooter: boolean = true;

    simpleCompareFields: (keyof DistributionModel)[] = [
        'name',
        'fromName',
        'replyEmail',
        'subjectLine',
        'previewText',
        'bodyPlainText',
        'footerPlainText',
        'includeViewInBrowserLink',
        'viewInBrowserLinkText',
        'individualRecipientsListId',
        'recommendedRecipientsListId',
        'clickTracking',
        'openTracking',
        'includeFooter',
        'onlineBoostApproved'
    ];

    constructor(apiModel?: DistributionApiModel) {
        super();
        // All distribs have a footer
        this.footerStyles = defaultFooterStyles;
        this.footer = new FooterModel();

        if (apiModel) {
            this.constructFromApiModel(apiModel);
        } else {
            this.constructDefault();
        }

        this.processGeneralStyles();
        if (!apiModel?.sendFrom || apiModel?.sendFrom?.includeFooter) {
            this.setFooterBlock(this.footer, this.footerStyles);
        }
    }

    constructFromApiModel(apiModel: DistributionApiModel): void {
        this.id = apiModel.id;
        this.name = apiModel.name;
        this.status = <DistributionStatus> apiModel.status;
        this.deliverySchedule = new DeliverySchedule(
            apiModel.scheduledDate,
            !!apiModel.timeZone ? apiModel.timeZone : null
        );
        this.recipientInformation = new RecipientInformationModel(apiModel);

        this.fromName = apiModel.fromName;
        this.replyEmail = apiModel.replyEmail;
        this.subjectLine = apiModel.subjectLine;
        this.previewText = apiModel.preheaderText;
        this.hidden = apiModel.hidden;
        if (apiModel.sendFrom) {
            this.sendFrom = new DistributionSendFrom(apiModel.sendFrom);
            this.includeFooter = apiModel.sendFrom.includeFooter;
        } else {
            this.includeFooter = true;
        }

        this.createdDate = apiModel.createdDate;
        this.createdByUserId = apiModel.createdUserId;
        this.modifiedDate = apiModel.modifiedDate;
        this.modifiedByUserId = apiModel.modifiedUserId;
        this.sentNow = apiModel.sentNow;
        this.sentDate = apiModel.sentDate;
        this.sentUserId = apiModel.sentUserId;
        this.bodyPlainText = apiModel.bodyPlainText;
        this.footerPlainText = apiModel.footerPlainText;

        this.includeViewInBrowserLink = apiModel.viewInBrowser;
        this.clickTracking = apiModel.enableClickTracking ?? true;
        this.openTracking = apiModel.enableOpenTracking ?? true;
        this.viewInBrowserLinkText = apiModel.viewInBrowserLinkText;
        this.guid = apiModel.guid;
        if (apiModel.aiReferenceId) {
            this.aiReferenceId = apiModel.aiReferenceId;
        } else {
            this.aiReferenceId = uuid.v4();
        }
        this.type = apiModel.type;

        if (apiModel.attachments) {
            this.attachments = [...apiModel.attachments];
        }

        if (apiModel.confirmationRecipients) {
            this.confirmationRecipients = apiModel.confirmationRecipients;
        }

        if (apiModel.footer) {
            this.footer = new FooterModel(apiModel.footer);

            if (apiModel.footer.styles) {
                this.footerStyles = apiModel.footer.styles;
            }
        }

        if (apiModel.blocks) {
            this.blocks = this.mapBlocksFromApi(apiModel.blocks);
        }

        this.generalStyles = new DistributionGeneralStylesModel(apiModel.generalStyles);
        const distributionMaxWidthPx = this.generalStyles.distributionMaxWidthPx;
        this.blocks.forEach(block => {
            block.displayElement.styles['maxWidthPx'] = distributionMaxWidthPx ? distributionMaxWidthPx : null;
        });

        this.individualRecipientsListId = apiModel.individualRecipientsListId;
        this.recommendedRecipientsListId = apiModel.recommendedRecipientsListId;
        this.excludedRecipients = apiModel.excludedRecipients ? [...apiModel.excludedRecipients] : [];
        this.mergeTags = apiModel.mergeTags
            ? apiModel.mergeTags.map(x => new MergeTagModel(x))
            : [];

        this.groupId = apiModel.displayGroupId;
        this.isFavorite = apiModel.isFavorited;
        this.onlineBoostApproved = apiModel.onlineBoostApproved;
    }

    constructDefault() {
        this.recipientInformation = new RecipientInformationModel();
        this.aiReferenceId = uuid.v4();
        this.generalStyles = new DistributionGeneralStylesModel();
    }

    toApiModel(): DistributionApiModel {
        // @HACK NEP-9096, body plain text can sometimes be an empty string, which is invalid from the backend's perspective
        // we should set it to null
        if (this.bodyPlainText === '') {
            this.bodyPlainText = null;
        }

        // @HACK NEP-9913, replyEmail can sometimes be an empty string, which is invalid email from the backend's perspective
        // we should set it to null
        if (this.replyEmail === '') {
            this.replyEmail = null;
        }

        const apiModel: DistributionApiModel = {
            id: this.id,
            name: this.name?.trim(),
            status: this.status,
            type: this.type,
            generalStyles: this.generalStyles ? JSON.stringify(this.generalStyles.toApi()) : '',
            timeZone: this.deliverySchedule.timeZone,
            createdDate: this.createdDate,
            createdUserId: this.createdByUserId,
            modifiedDate: this.modifiedDate,
            modifiedUserId: this.modifiedByUserId,
            sentNow: this.sentNow,
            hidden: this.hidden,
            sentDate: this.sentDate,
            sentUserId: this.sentUserId,
            recipients: this.recipientInformation ? this.recipientInformation.toApiRecipientModel() : [],
            adhocRecipients: this.recipientInformation ? this.recipientInformation.toApiAdhocRecipients() : [],
            fromName: this.fromName,
            replyEmail : this.replyEmail,
            subjectLine : this.subjectLine,
            preheaderText : this.previewText,
            attachments: [...this.attachments],
            confirmationRecipients: [...this.confirmationRecipients],
            bodyPlainText: this.bodyPlainText,
            footerPlainText: this.footerPlainText,
            viewInBrowser: this.includeViewInBrowserLink,
            viewInBrowserLinkText: this.viewInBrowserLinkText,
            enableClickTracking: this.clickTracking,
            enableOpenTracking: this.openTracking,
            guid: this.guid,
            aiReferenceId: this.aiReferenceId,
            individualRecipientsListId: this.individualRecipientsListId,
            recommendedRecipientsListId: this.recommendedRecipientsListId,
            excludedRecipients: [...this.excludedRecipients],
            scheduledDate: this.deliverySchedule.scheduleType === ScheduleType.immediate
                ? null
                : this.deliverySchedule.scheduledDateUTC,
            mergeTags: this.mergeTags.map(x => x.toApiModel()),
            displayGroupId: this.groupId,
            isFavorited: this.isFavorite,
            sendFrom: this.sendFrom?.connectionId ? this.sendFrom?.toApiModel() : null,
            onlineBoostApproved: this.onlineBoostApproved
        };

        if (apiModel.sendFrom) {
            apiModel.sendFrom.includeFooter = this.includeFooter;
        }

        this.mapBlocksToApi(this.blocks, apiModel);
        return apiModel;
    }

    clone(): DistributionModel {
        const clone = new DistributionModel(this.toApiModel());
        clone.recipientInformation = this.recipientInformation;
        if (clone.sendFrom) {
            clone.sendFrom.connectionName = this.sendFrom.connectionName;
        }

        // remove lock state of any searches
        clone.recipientInformation.recipientGroups
            .filter(x => x.type === RecipientType.search)
            .forEach(searchGroup => {
                searchGroup.recipients.forEach((value, index, array) => {
                    if (value.targetingSearchId && value.targetingListId) {
                        array[index].targetingListId = null;
                        array[index].isLocked = false;
                    }
                });
            });

        clone.onlineBoostApproved = false;

        return clone;
    }

    equals(other?: DistributionModel): boolean {
        if (!other) {
            return false;
        }

        // Compare simple fields
        for (let i = 0; i < this.simpleCompareFields.length; i++) {
            const field = this.simpleCompareFields[i];
            if (this[field] !== other[field]) {
                return false;
            }
        }

        if (!this.localAttachmentChanges && this.attachments.length === other.attachments.length) {
            for (const element of this.attachments) {
                if (!other.attachments.find(x => element === x)) {
                    return false;
                }
            }
        } else {
            return false;
        }

        // Compare confirmation recipients
        if (this.confirmationRecipients.length === other.confirmationRecipients.length) {
            for (const element of this.confirmationRecipients) {
                if (!other.confirmationRecipients.find(x => element === x)) {
                    return false;
                }
            }
        } else {
            return false;
        }

        // Compare blocks
        if (this.blocks.length !== other.blocks.length) {
            return false;
        }

        for (let i = 0; i < this.blocks.length; i++) {
            const thisBlock = this.blocks[i];
            const otherBlock = other.blocks[i];

            if (thisBlock.id !== otherBlock.id) {
                return false;
            }

            if (!thisBlock.type || !otherBlock.type || thisBlock.type.key !== otherBlock.type.key) {
                return false;
            }

            if (!thisBlock.displayElement.isEqual(otherBlock.displayElement)) {
                return false;
            }

            if (thisBlock.isLayoutBlockType()) {
                const thisLayoutBlock = thisBlock as LayoutBlock;
                const otherLayoutBlock = otherBlock as LayoutBlock;

                if (thisLayoutBlock.blocks?.length !== otherLayoutBlock.blocks?.length) {
                    return false;
                }

                for (let columnIndex = 0; columnIndex < thisLayoutBlock.blocks.length; columnIndex++) {
                    const thisColumn = thisLayoutBlock.blocks[columnIndex];
                    const otherColumn = otherLayoutBlock.blocks[columnIndex];
                    if (thisColumn.length !== otherColumn.length) {
                        return false;
                    }
                    for (let positionInColumn = 0; positionInColumn < thisColumn.length; positionInColumn++) {
                        const thisNestedBlock = thisColumn[positionInColumn];
                        const otherNestedBlock = otherColumn[positionInColumn];

                        if (thisNestedBlock.id !== otherNestedBlock.id) {
                            return false;
                        }

                        if (!thisNestedBlock.type || !otherNestedBlock.type || thisNestedBlock.type.key !== otherNestedBlock.type.key) {
                            return false;
                        }

                        if (!thisNestedBlock.displayElement.isEqual(otherNestedBlock.displayElement)) {
                            return false;
                        }
                    }
                }
            }
        }

        if (!this.recipientInformation.equals(other.recipientInformation)) {
            return false;
        }

        if (!this.deliverySchedule.equals(other.deliverySchedule)) {
            return false;
        }

        if (!this.generalStyles.equals(other.generalStyles)) {
            return false;
        }

        if (!isEqual(this.excludedRecipients, other.excludedRecipients)) {
            return false;
        }

        if (JSON.stringify(this.sendFrom) !== JSON.stringify(other.sendFrom)) {
            return false;
        }

        if (JSON.stringify(this.mergeTags) !== JSON.stringify(other.mergeTags)) {
            return false;
        }
        return true;
    }

    summaryEquals(other?: DistributionModel): boolean {
        return this.name === other.name && this.status === other.status;
    }

    mapBlocksToApi(blocks: BlockModel[], apiModel: DistributionApiModel): DistributionBlockApiModel[] {
        const apiBlocks: DistributionBlockApiModel[] = [];
        for (const block of blocks) {
            const apiBlock = this.mapBlockToApi(block, apiModel);
            if (apiBlock) {
                apiBlocks.push(apiBlock);
            }
        }
        apiModel.blocks = apiBlocks;

        return apiBlocks;
    }

    mapBlockToApi(block: BlockModel, apiModel: DistributionApiModel): DistributionBlockApiModel {
        let apiBlock = null;
        if (!block.type || !block.type.key) {
            return;
        }

        let styles;
        switch (block.type.key) {
            case DistributionBlockTypeEnum.title:
                const titleDisplayElement = block.displayElement as RichTextDisplayElementModel;

                styles = titleDisplayElement.styles.toBlockStyle();
                styles.isBoundToGlobalStyles = titleDisplayElement.isBoundToGlobalStyles;
                styles.htmlEdit = titleDisplayElement.htmlEdit;

                apiBlock = {
                    type: distributionBlockTypeEnumToDistributionBlockTypeApiEnum.get(block.type.key as DistributionBlockTypeEnum),
                    styles: JSON.stringify(styles),
                    title: titleDisplayElement.text === '' ? null : titleDisplayElement.text,
                    customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null
                } as DistributionTitleDisplayElementApiModel;
                break;
            case DistributionBlockTypeEnum.text:
                const textDisplayElement = block.displayElement as RichTextDisplayElementModel;

                // hack @TODO NEP-3392
                styles = textDisplayElement.styles.toBlockStyle();
                styles.isBoundToGlobalStyles = textDisplayElement.styles.isBoundToGlobalStyles;
                styles.htmlEdit = textDisplayElement.htmlEdit;

                if (styles.styleState === StyleStateEnum.DEFAULT) {
                    styles.styleState = DistributionStyleStateEnum.DISTRIBUTION_1;
                }

                if (styles.displayStyleState === StyleStateEnum.DEFAULT) {
                    styles.displayStyleState = DistributionStyleStateEnum.DISTRIBUTION_1;
                }
                // endhack

                apiBlock = {
                    type: distributionBlockTypeEnumToDistributionBlockTypeApiEnum.get(block.type.key as DistributionBlockTypeEnum),
                    styles: JSON.stringify(styles),
                    text: textDisplayElement.text === '' ? null : textDisplayElement.text,
                    customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null
                } as DistributionTextDisplayElementApiModel;
                break;
            case DistributionBlockTypeEnum.image:
                const imageDisplayElement = block.displayElement as ImageDisplayElementModel;

                // hack @TODO NEP-3392
                styles = imageDisplayElement.styles.toBlockStyle();
                styles.isBoundToGlobalStyles = imageDisplayElement.isBoundToGlobalStyles;

                apiBlock = {
                    type: distributionBlockTypeEnumToDistributionBlockTypeApiEnum.get(block.type.key as DistributionBlockTypeEnum),
                    styles: JSON.stringify(styles),
                    // TODO: remove when 500s in the backend are fixed, currently can't be null
                    url: imageDisplayElement.image?.url ? imageDisplayElement.image.url : '',
                    width: imageDisplayElement.width,
                    customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null,
                    link: imageDisplayElement.settings?.link?.toApi(),
                    displaySize: imageDisplayElement.settings?.displaySize,
                    caption: imageDisplayElement.settings?.caption ? imageDisplayElement.settings?.caption : null,
                    altText: imageDisplayElement.image?.altText || null ,
                    // imageAlignment: imageDisplayElement.settings?.imageAlignment,
                } as DistributionImageDisplayElementApiModel;
                break;
            case DistributionBlockTypeEnum.button:
                const buttonDisplayElement = block.displayElement as ButtonDisplayElementModel;

                // hack @TODO NEP-3392
                styles = buttonDisplayElement.styles.toBlockStyle();
                styles.isBoundToGlobalStyles = buttonDisplayElement.isBoundToGlobalStyles;
                // End hack

                apiBlock = {
                    type: distributionBlockTypeEnumToDistributionBlockTypeApiEnum.get(block.type.key as DistributionBlockTypeEnum),
                    styles: JSON.stringify(styles),
                    // TODO: remove when 500s in the backend are fixed, currently can't be null
                    url: buttonDisplayElement.linkToUrlString(buttonDisplayElement.url),
                    text: buttonDisplayElement.text ?? '',
                    customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null
                } as DistributionButtonDisplayElementApiModel;

                break;
            case DistributionBlockTypeEnum.footer:
                const footerDisplayElement = block.displayElement as FooterDisplayElementModel;
                footerDisplayElement.footer.text = footerDisplayElement.text;
                apiModel.footer = footerDisplayElement.footer.toApiModel(footerDisplayElement.styles);
                break;
            case DistributionBlockTypeEnum.divider:
                const dividerDisplayElement = block.displayElement as DividerDisplayElementModel;

                // hack @TODO NEP-3392
                styles = dividerDisplayElement.styles.toBlockStyle();
                styles.isBoundToGlobalStyles = dividerDisplayElement.isBoundToGlobalStyles;
                // endhack

                apiBlock = {
                    type: distributionBlockTypeEnumToDistributionBlockTypeApiEnum.get(block.type.key as DistributionBlockTypeEnum),
                    styles: JSON.stringify(styles),
                    customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null
                } as DistributionDividerDisplayElementApiModel;

                break;
            case DistributionBlockTypeEnum.link:
                const linkDisplayElement = block.displayElement as LinkDisplayElementModel;
                styles = linkDisplayElement.styles.toBlockStyle();
                styles.isBoundToGlobalStyles = linkDisplayElement.isBoundToGlobalStyles;
                apiBlock = {
                    type: distributionBlockTypeEnumToDistributionBlockTypeApiEnum.get(block.type.key as DistributionBlockTypeEnum),
                    styles: JSON.stringify(styles),
                    links: linkDisplayElement.links.map( link => link.toApi()),
                    customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null
                } as DistributionLinkDisplayElementApiModel;
                break;
            case DistributionBlockTypeEnum.oneColumnLayout:
            case DistributionBlockTypeEnum.twoColumnLayout:
            case DistributionBlockTypeEnum.threeColumnLayout:
                const layoutDisplayElement = block.displayElement as LayoutDisplayElement;
                styles = layoutDisplayElement.styles.toBlockStyle();
                styles.isBoundToGlobalStyles = layoutDisplayElement.isBoundToGlobalStyles;
                apiBlock = {
                    type: distributionBlockTypeEnumToDistributionBlockTypeApiEnum.get(block.type.key as DistributionBlockTypeEnum),
                    styles: JSON.stringify(styles),
                    customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null,
                    columns: (block as LayoutBlock).blocks.map(column => {
                        return {
                            blocks: column.map(x => this.mapBlockToApi(x, apiModel))
                        };
                    })
                } as DistributionBlockApiModel;
                break;
            default:
                // Unknown block type, do nothing
                break;
        }

        return apiBlock;
    }

    setFooterBlock(footer: FooterModel, footerStyles: any = defaultFooterStyles): void {
        if (this.blocks?.length && this.blocks[this.blocks.length - 1].type.key === DistributionBlockTypeEnum.footer) {
            // Take the footer off it's already set
            this.blocks.pop();
        }

        const footerBlock = new BlockModel(
            null,
            distributionFooterBlockType,
            new FooterDisplayElementModel(footer, footerStyles)
        );
        const distributionMaxWidthPx = this.generalStyles.distributionMaxWidthPx;
        footerBlock.displayElement.styles['maxWidthPx'] = distributionMaxWidthPx ? distributionMaxWidthPx : null;

        footerBlock.canAddOrRemove = false;
        footerBlock.canMove = false;
        footerBlock.canEdit = true;
        footerBlock.canDuplicate = false;

        this.blocks.push(
            footerBlock
        );
    }

    getFooterBlock(): BlockModel {
        return this.blocks.find(block => block.type.key === DistributionBlockTypeEnum.footer);
    }

    mapBlocksFromApi(apiBlocks: DistributionBlockApiModel[] = []): BlockModel[] {
        const blocks = [];

        for (const apiBlock of apiBlocks) {
            let block;
            const blockType = distributionBlockTypeEnumToBlockTypeMap.get(distributionBlockTypeApiEnumToDistributionBlockTypeEnum.get(apiBlock.type));

            switch (blockType.key) {
                case DistributionBlockTypeEnum.title:
                    const titleApiData: DistributionTitleDisplayElementApiModel = apiBlock as DistributionTitleDisplayElementApiModel;
                    block = new BlockModel(
                        null,
                        blockType,
                        new RichTextDisplayElementModel(titleApiData.title, titleApiData.styles)
                    );

                    const titleStylesObject = JSON.parse(titleApiData.styles);
                    block.displayElement.htmlEdit = titleStylesObject['htmlEdit'];

                    break;
                case DistributionBlockTypeEnum.text:
                    const textApiData: DistributionTextDisplayElementApiModel = apiBlock as DistributionTextDisplayElementApiModel;
                    block = new BlockModel(
                        null,
                        blockType,
                        new RichTextDisplayElementModel(textApiData.text, textApiData.styles)
                    );

                    const stylesObject = JSON.parse(textApiData.styles);
                    block.displayElement.htmlEdit = stylesObject['htmlEdit'];
                    (block.displayElement as RichTextDisplayElementModel).enableAIHelper = true;
                    (block.displayElement as RichTextDisplayElementModel).aiReferenceId = this.aiReferenceId;

                    if (!stylesObject.styleState || stylesObject.styleState === StyleStateEnum.DEFAULT) {
                        block.displayElement.styles.styleState = DistributionStyleStateEnum.DISTRIBUTION_1;
                    }

                    if (!stylesObject.displayStyleState && stylesObject.styleState) {
                        stylesObject.displayStyleState = stylesObject.styleState;
                    }

                    if (!stylesObject.displayStyleState || stylesObject.displayStyleState === StyleStateEnum.DEFAULT) {
                        block.displayElement.styles.displayStyleState = DistributionStyleStateEnum.DISTRIBUTION_1;
                    }

                    break;
                case DistributionBlockTypeApiEnum.button:
                    const buttonApiData: DistributionButtonDisplayElementApiModel = apiBlock as DistributionButtonDisplayElementApiModel;
                    const urlLink = new LinkModel();

                    block = new BlockModel(
                        apiBlock.id,
                        blockType,
                        new ButtonDisplayElementModel(buttonApiData.text, urlLink.decodeUrl(buttonApiData.url), buttonApiData.styles)
                    );

                    break;
                case DistributionBlockTypeApiEnum.image:
                    const imageApiData: ImageDisplayElementApiModel = apiBlock as DistributionImageDisplayElementApiModel;
                    block = new BlockModel(
                        apiBlock.id,
                        blockType,
                        new ImageDisplayElementModel(imageApiData)
                    );

                    break;

                case DistributionBlockTypeEnum.divider:
                    const dividerApiData: DistributionDividerDisplayElementApiModel = apiBlock as DistributionDividerDisplayElementApiModel;
                    block = new BlockModel(
                        null,
                        blockType,
                        new DividerDisplayElementModel(dividerApiData.styles)
                    );
                    break;
                case DistributionBlockTypeEnum.link:
                    const linkApiData: DistributionLinkDisplayElementApiModel = apiBlock as DistributionLinkDisplayElementApiModel;
                    block = new BlockModel(
                        null,
                        blockType,
                        new LinkDisplayElementModel(linkApiData.styles, linkApiData.links)
                    );
                    break;
                case DistributionBlockTypeEnum.oneColumnLayout:
                case DistributionBlockTypeEnum.twoColumnLayout:
                case DistributionBlockTypeEnum.threeColumnLayout:
                    block = new LayoutBlock(
                        null,
                        blockType as LayoutBlockType,
                        new LayoutDisplayElement(apiBlock.styles),
                        apiBlock.columns.map(column => {
                            return this.mapBlocksFromApi(column.blocks);
                        })
                    );
            }

            if (apiBlock.styles) {
                // backend sometimes passes "null"
                const styles = JSON.parse(apiBlock.styles);
                if (styles) {
                    // @ TODO - NEP-3392 hack should add isBoundToGlobalStyles to
                    block.displayElement.isBoundToGlobalStyles = styles.isBoundToGlobalStyles === true || styles.isBoundToGlobalStyles === 'true';
                } else {
                    block.displayElement.isBoundToGlobalStyles = true;
                }
            } else {
                block.displayElement.isBoundToGlobalStyles = true;
            }

            blocks.push(block);
        }

        return blocks;
    }

    /**
     * update recipientInformation based on excludedRecipients
     */
    public updateRecipientGroupCount(recipientType: RecipientType): void {
        this.excludedRecipients?.forEach(excludeRecipient => {
            this.recipientInformation.recipientGroups.forEach(recipientGroup => {
                recipientGroup.recipients.forEach(recipient => {
                    let hasExcludedContact = false;

                    if (recipientType === RecipientType.list
                        && excludeRecipient.listId
                        && excludeRecipient.listId !== this.individualRecipientsListId
                        && excludeRecipient.listId !== this.recommendedRecipientsListId
                        && recipient.targetingListId === excludeRecipient.listId) {
                        hasExcludedContact = true;
                    }

                    if (recipientType === RecipientType.search && excludeRecipient.searchId && recipient.targetingSearchId === excludeRecipient.searchId) {
                        hasExcludedContact = true;
                    }

                    if (recipientType === RecipientType.individual
                        && excludeRecipient.listId
                        && excludeRecipient.listId === this.individualRecipientsListId
                        && recipient.targetingListId === excludeRecipient.listId) {
                        hasExcludedContact = true;
                    }

                    if (recipientType === RecipientType.recommended
                        && excludeRecipient.listId
                        && excludeRecipient.listId === this.recommendedRecipientsListId
                        && recipient.targetingListId === excludeRecipient.listId) {
                        hasExcludedContact = true;
                    }

                    if (hasExcludedContact) {
                        // we don't want to see -1.. there is a change that you exclude a user from a recommended list, then remove them from that list
                        if (recipient.count > 0) {
                            recipient.count--;
                            recipientGroup.total--;
                        }
                        if (excludeRecipient.contactId && recipient.contactCount > 0) {
                            recipient.contactCount--;
                        }
                        if (excludeRecipient.outletId && recipient.outletCount > 0) {
                            recipient.outletCount--;
                        }
                    }
                });
            });
        });

        this.recipientInformation.calculateTotals();
    }

    /**
     * For distribution, it does not have default style state, the default is DISTRIBUTION_1
     */
    private processGeneralStyles(): void {
        this.generalStyles.getStyles().forEach(style => {
            if (!style.styleState || style.styleState === StyleStateEnum.DEFAULT ) {
                style.styleState = DistributionStyleStateEnum.DISTRIBUTION_1;
            }
        });
    }

    public anonymize() {
        this.id = null;
        this.createdByUserId = null;
        this.createdDate = null;
        this.modifiedByUserId = null;
        this.modifiedDate = null;
        this.aiReferenceId = null;
        this.status = DistributionStatus.draft;
        for (const block of this.blocks){
            // image blocks may have the link attribute, we need to delete the id of the link otherwise b/e will not accept the copy request
            delete (block as any).link?.id;
        }

    }
}
